import { btn } from './components';
import { h } from './dom';
import type { Child } from './dom';

export interface Modal {
  el: HTMLDialogElement;
  open: () => void;
  close: () => void;
  /** Takes the dialog out of the page, for when its screen goes away. */
  remove: () => void;
}

/** A modal dialog (D66): a title, a Close button, the content. Escape closes it (the browser's own),
 *  and so does a click on the dimmed page around it. Engines without showModal show it in place.
 *  While it is open the shell's toasts show inside it (`ui/shell.ts`). */
export const modal = (title: string, ...content: Child[]): Modal => {
  const close = () => (typeof el.close === 'function' ? el.close() : el.removeAttribute('open'));
  const el = h(
    'dialog',
    { class: 'modal', 'aria-label': title },
    h(
      'div',
      { class: 'modal__inner' },
      h(
        'header',
        { class: 'modal__head' },
        h('h2', { class: 'modal__title' }, title),
        btn('Close', { variant: 'subtle', size: 'sm', onClick: close }),
      ),
      h('div', { class: 'modal__body' }, content),
    ),
  );
  // a click on the backdrop targets the dialog; so does a press inside that ends outside it (a text
  // selection), so it closes only when the press too was outside the dialog's box
  let pressedOutside = false;
  const outside = (e: MouseEvent) => {
    const r = el.getBoundingClientRect();
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
  };
  el.addEventListener('mousedown', (e) => {
    pressedOutside = e.target === el && outside(e);
  });
  el.addEventListener('click', (e) => {
    if (pressedOutside && e.target === el && outside(e)) close();
    pressedOutside = false;
  });
  const open = () => {
    if (!el.isConnected) document.body.append(el);
    if (typeof el.showModal === 'function') {
      if (!el.open) el.showModal();
    } else el.setAttribute('open', '');
  };
  return { el, open, close, remove: () => el.remove() };
};
